import { api } from "@/lib/api";
import type { AxiosResponse } from "axios";

interface ApiResponse<T> {
	data: T;
	message?: string;
}

export type SocialChannelRow = {
	id: string;
	hospitalId: string;
	platform: string;
	externalAccountId: string;
	pageId: string | null;
	accessTokenMasked: string | null;
	hasAccessToken: boolean;
	aiEnabled: boolean;
	leadThreshold: number;
	autoReplyEnabled: boolean;
	defaultAssigneeTeamIds: string[];
	status: "ACTIVE" | "INACTIVE";
	createdAt: string;
	updatedAt: string;
};

export type SocialInboxItem = {
	id: string;
	hospitalId: string;
	channelId: string;
	igUserId: string;
	username: string | null;
	conversationType: string;
	status: string;
	lastMessagePreview: string | null;
	lastMessageAt: string | null;
	postId: string | null;
	reelId: string | null;
	mediaId: string | null;
	socialLead: {
		id: string;
		classification: string;
		confidence: number;
		procedure: string | null;
		priority: string;
		reason: string | null;
		assignedToId: string | null;
		leadId: string | null;
		assignedTo?: { id: string; name: string } | null;
		lead?: { id: string; status: string; patientName: string } | null;
	} | null;
	channel?: { id: string; externalAccountId: string; status: string };
};

export type SocialAnalytics = {
	organicLeadCount: number;
	crmLinkedSocialLeads: number;
	byClassification: Array<{ classification: string; count: number }>;
	topProcedures: Array<{ procedure: string | null; count: number }>;
	byMedia: Array<{ mediaId: string | null; count: number }>;
	conversationByStatus: Array<{ status: string; count: number }>;
	webhookCallbackUrl?: string;
	verifyTokenConfigured?: boolean;
};

export type SocialMessageRow = {
	id: string;
	direction: string;
	text: string | null;
	sentAt: string;
};

export type SocialConversationDetail = {
	id: string;
	hospitalId: string;
	channelId: string;
	igUserId: string;
	username: string | null;
	conversationType: string;
	status: string;
	lastMessagePreview: string | null;
	lastMessageAt: string | null;
	postId: string | null;
	reelId: string | null;
	mediaId: string | null;
	messages: SocialMessageRow[];
	socialLead: {
		id: string;
		classification: string;
		confidence: number;
		procedure: string | null;
		priority: string;
		reason: string | null;
		assignedToId: string | null;
		leadId: string | null;
		assignedTo?: { id: string; name: string; role?: string } | null;
		lead?: {
			id: string;
			status: string;
			patientName: string;
			phone?: string;
			source?: string;
		} | null;
	} | null;
	channel?: {
		id: string;
		externalAccountId: string;
		pageId?: string | null;
		leadThreshold?: number;
		status: string;
	};
};

export type SocialWebhookMeta = {
	hospitalId: string;
	callbackUrl: string;
	verifyTokenEnv: string;
	appSecretEnv: string;
	subscribeFields: string[];
};

function hospitalParams(hospitalId?: string) {
	return hospitalId ? { hospitalId } : undefined;
}

function unwrap<T>(r: AxiosResponse<ApiResponse<T>>): T {
	return r.data.data;
}

export const socialApi = {
	listChannels: (hospitalId?: string) =>
		api
			.get<ApiResponse<SocialChannelRow[]>>("/api/social/channels", {
				params: hospitalParams(hospitalId),
			})
			.then(unwrap),

	upsertChannel: (
		payload: {
			id?: string;
			externalAccountId: string;
			pageId?: string | null;
			accessToken?: string | null;
			aiEnabled?: boolean;
			leadThreshold?: number;
			autoReplyEnabled?: boolean;
			defaultAssigneeTeamIds?: string[];
			status?: "ACTIVE" | "INACTIVE";
		},
		hospitalId?: string
	) =>
		api
			.post<ApiResponse<SocialChannelRow>>("/api/social/channels", payload, {
				params: hospitalParams(hospitalId),
			})
			.then(unwrap),

	deleteChannel: (id: string, hospitalId?: string) =>
		api
			.delete<ApiResponse<{ ok: boolean }>>(`/api/social/channels/${id}`, {
				params: hospitalParams(hospitalId),
			})
			.then(unwrap),

	listInbox: (
		params: {
			status?: string;
			conversationType?: string;
			classification?: string;
			assigned?: string;
			procedure?: string;
			from?: string;
			to?: string;
			q?: string;
			take?: number;
			skip?: number;
			hospitalId?: string;
		} = {}
	) =>
		api
			.get<
				ApiResponse<{
					total: number;
					take: number;
					skip: number;
					items: SocialInboxItem[];
				}>
			>("/api/social/inbox", { params })
			.then(unwrap),

	getInbox: (id: string, hospitalId?: string) =>
		api
			.get<ApiResponse<SocialConversationDetail>>(`/api/social/inbox/${id}`, {
				params: hospitalParams(hospitalId),
			})
			.then(unwrap),

	inboxAction: (
		id: string,
		body: { action: "convert" | "ignore" | "close" | "assign"; assignedToId?: string },
		hospitalId?: string
	) =>
		api
			.post<ApiResponse<{ action: string; ok?: boolean; leadId?: string | null }>>(
				`/api/social/inbox/${id}/action`,
				body,
				{
					params: hospitalParams(hospitalId),
				}
			)
			.then(unwrap),

	analytics: (params?: { from?: string; to?: string; hospitalId?: string }) =>
		api
			.get<ApiResponse<SocialAnalytics>>("/api/social/analytics", { params })
			.then(unwrap),

	webhookMeta: (hospitalId?: string) =>
		api
			.get<ApiResponse<SocialWebhookMeta>>("/api/social/webhook-meta", {
				params: hospitalParams(hospitalId),
			})
			.then(unwrap),

	diagnoseChannel: (
		channelId: string,
		body?: { recipientIgsid?: string; sendTestMessage?: boolean },
		hospitalId?: string
	) =>
		api
			.post<ApiResponse<Record<string, unknown>>>(
				`/api/social/channels/${channelId}/diagnose`,
				body ?? {},
				{ params: hospitalParams(hospitalId) }
			)
			.then(unwrap),

	forcePhoneAsk: (conversationId: string, hospitalId?: string) =>
		api
			.post<ApiResponse<Record<string, unknown>>>(
				`/api/social/inbox/${conversationId}/force-phone-ask`,
				{},
				{ params: hospitalParams(hospitalId) }
			)
			.then(unwrap),
};
